import http from './api';
import { refreshAccessTokenWithDedup } from './authErrorHandler';
import { LiteratureOptions } from '../schemas/chat';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * Fetch API wrapper with automatic token refresh
 */
async function fetchWithAuth(
  url: string,
  options: RequestInit = {},
  retryCount = 0
): Promise<Response> {
  const token = localStorage.getItem('access_token');

  const headers = new Headers(options.headers);
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  headers.set('Content-Type', 'application/json');
  headers.set('Accept', 'text/event-stream');

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Handle 401 or 403 errors with token refresh
  if ((response.status === 401 || response.status === 403) && retryCount === 0) {
    try {
      const newToken = await refreshAccessTokenWithDedup(async () => {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        // Use axios to refresh token (it has interceptors)
        const response = await http.post('/api/v1/auth/refresh', {
          refresh_token: refreshToken,
        });

        return response.data as {
          access_token: string;
          refresh_token?: string;
        };
      });

      // Retry the request with new token
      headers.set('Authorization', `Bearer ${newToken}`);
      return fetchWithAuth(url, { ...options, headers }, retryCount + 1);
    } catch (refreshError) {
      // Token refresh failed - auth error handler already handled redirect
      // Return the original error response
      return response;
    }
  }

  if (!response.ok && response.status !== 200) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response;
}

/**
 * Stream message request payload type
 */
export interface StreamMessageRequest {
  content: string;
  model_id?: string | null;
  knowledge_base_ids?: string[];
  files?: (string | Record<string, any>)[];
  enable_thinking?: boolean;
  temperature?: number;
  max_tokens?: number;
  stream?: boolean;
  literature_sources?: string[];
  literature_options?: LiteratureOptions;
}

/**
 * Stream a message using Fetch API with automatic token refresh
 * @param conversationId - The conversation ID
 * @param data - The message data to send
 * @param basePath - Optional base path for the API (default: '/api/v1/chats')
 * @returns A Response object with a ReadableStream body
 */
export async function streamMessage<T extends StreamMessageRequest = StreamMessageRequest>(
  conversationId: string,
  data: T,
  basePath: string = '/api/v1/chats'
): Promise<Response> {
  const url = `${API_BASE_URL}${basePath}/${conversationId}/messages/stream`;

  const response = await fetchWithAuth(url, {
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!response.body) {
    throw new Error('Response body is null');
  }

  return response;
}

/**
 * Process SSE stream and call callbacks
 * @param reader - ReadableStreamDefaultReader
 * @param onChunk - Callback for each content chunk
 * @param onThinkingChunk - Callback for thinking chunks
 * @param onComplete - Callback when stream completes
 * @param onTitleChunk - Callback for title chunks
 * @returns Parsed message data
 */
export async function processStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  onChunk?: (chunk: string) => void,
  onThinkingChunk?: (chunk: string) => void,
  onComplete?: (message: {
    id: string;
    role: 'assistant';
    content: string;
    thinking_process?: { steps: number; content: string };
    created_at: string;
    model_name?: string;
    is_done: boolean;
  }) => void,
  onTitleChunk?: (title: string) => void
): Promise<{
  id: string;
  role: 'assistant';
  content: string;
  thinking_process?: { steps: number; content: string };
  created_at: string;
  model_name?: string;
  is_done: boolean;
}> {
  const decoder = new TextDecoder();
  let buffer = '';
  let accumulatedContent = '';
  let accumulatedThinking = '';
  let messageId = '';
  let model = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      // Decode the chunk and immediately process it
      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;

      // Split by newlines and process complete lines
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      // Process each line immediately
      for (const line of lines) {
        if (line.trim() && line.startsWith('data: ')) {
          const data = line.slice(6).trim();

          if (data === '[DONE]') {
            const finalMessage = {
              id: messageId,
              role: 'assistant' as const,
              content: accumulatedContent,
              created_at: new Date().toISOString(),
              model_name: model || undefined,
              is_done: true,
            };
            if (onComplete) {
              onComplete(finalMessage);
            }
            return finalMessage;
          }

          // Skip empty data
          if (!data) continue;

          try {
            const parsed = JSON.parse(data);

            // Handle new event-based format with type field
            if (parsed.type === 'content') {
              // Content event: accumulate content and emit chunk
              if (parsed.content) {
                accumulatedContent += parsed.content;
                if (onChunk) {
                  onChunk(parsed.content);
                }
              }
              // Update messageId and model if provided
              if (parsed.id) messageId = parsed.id;
              if (parsed.model) model = parsed.model;
            } else if (parsed.type === 'thinking') {
              // Thinking event: accumulate thinking content and emit thinking chunk
              if (parsed.content) {
                accumulatedThinking += parsed.content;
                console.log('🧠 Received thinking chunk:', parsed.content);
                if (onThinkingChunk) {
                  onThinkingChunk(parsed.content);
                }
              }
              // Update messageId and model if provided - support both id and message_id fields
              if (parsed.id) {
                messageId = parsed.id;
              } else if (parsed.message_id) {
                messageId = parsed.message_id;
              }
              if (parsed.model) model = parsed.model;
            } else if (parsed.type === 'title') {
              // Title event: emit title chunk immediately
              if (parsed.content && onTitleChunk) {
                onTitleChunk(parsed.content);
              }
            } else if (parsed.type === 'done') {
              // Done event: stream is complete, use message_id from done event
              if (parsed.message_id) {
                messageId = parsed.message_id;
              }
              // Update model if provided
              if (parsed.model) model = parsed.model;

              const finalMessage = {
                id: messageId,
                role: 'assistant' as const,
                content: accumulatedContent,
                thinking_process: accumulatedThinking
                  ? {
                      steps: accumulatedThinking.split('\n').length,
                      content: accumulatedThinking,
                    }
                  : undefined,
                created_at: new Date().toISOString(),
                model_name: model || undefined,
                is_done: true,
              };
              if (onComplete) {
                onComplete(finalMessage);
              }
              return finalMessage;
            }
          } catch (e) {
            console.warn('Failed to parse streaming data:', data, e);
          }
        }
      }
    }

    // Return final message if stream completed without [DONE]
    const finalMessage = {
      id: messageId || `msg-${Date.now()}`,
      role: 'assistant' as const,
      content: accumulatedContent,
      thinking_process: accumulatedThinking
        ? {
            steps: accumulatedThinking.split('\n').length,
            content: accumulatedThinking,
          }
        : undefined,
      created_at: new Date().toISOString(),
      model_name: model || undefined,
      is_done: true,
    };
    if (onComplete) {
      onComplete(finalMessage);
    }
    return finalMessage;
  } catch (error) {
    console.error('Stream processing error:', error);
    throw error;
  } finally {
    // 安全地释放 reader 锁，避免重复释放导致的错误
    try {
      reader.releaseLock();
    } catch (e) {
      // Reader 可能已经被释放，忽略错误
      console.debug('Reader lock already released');
    }
  }
}
